import { useCallback, useEffect, useState } from 'react'
import {
  fetchCurrentVulnerabilityPosture,
  fetchVulnerabilityTrend,
  type CurrentVulnerabilityPosture,
  type VulnerabilityTrendPoint,
} from '../services/vulnerabilityPostureApi'

type State = {
  posture: CurrentVulnerabilityPosture | null
  trend: VulnerabilityTrendPoint[]
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

export function useCurrentVulnerabilityPosture(enabled = true, trendDays = 90): State {
  const [posture, setPosture] = useState<CurrentVulnerabilityPosture | null>(null)
  const [trend, setTrend] = useState<VulnerabilityTrendPoint[]>([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const [current, trendData] = await Promise.all([
        fetchCurrentVulnerabilityPosture(),
        fetchVulnerabilityTrend(trendDays),
      ])
      setPosture(current)
      setTrend(trendData)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load vulnerability posture')
      setPosture(null)
      setTrend([])
    } finally {
      setLoading(false)
    }
  }, [enabled, trendDays])

  useEffect(() => {
    void reload()
  }, [reload])

  return { posture, trend, loading, error, reload }
}
